import { FRAME_BUDGET_MS } from './constants'
import type { Env } from './env'
import type { Gfx } from './gfx'
import { instrument } from './guard'

export type DrawFn = (g: Gfx, t: number, env: Env) => void

export type CompiledPlot = {
  readonly draw: DrawFn
  /** The instrumented source that actually runs. */
  readonly source: string
}

export type CompileOptions = {
  budgetMs?: number
  now?: () => number
}

/** Thrown from inside draw() when a frame runs past its CPU budget. */
export class OverheatError extends Error {
  constructor(budgetMs: number) {
    super(`plot ran past its ${budgetMs}ms frame budget`)
    this.name = 'OverheatError'
  }
}

/** Thrown when code parses but is not a usable plot. */
export class PlotError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PlotError'
  }
}

/** How many __tick() calls happen between clock reads. */
const TICK_CHECK_INTERVAL = 256

/** Globals a plot must never see. Each becomes an `undefined` parameter. */
const SHADOWED_GLOBALS = [
  'Date',
  'fetch',
  'XMLHttpRequest',
  'WebSocket',
  'importScripts',
  'self',
  'globalThis',
  'window',
  'postMessage',
  'setTimeout',
  'setInterval',
  'setImmediate',
  'queueMicrotask',
  'Promise',
  'Function',
  'Reflect',
  'Proxy',
  'indexedDB',
  'caches',
  'navigator',
  'location',
] as const

/** Math without random: plots get determinism through g.rnd instead. */
export const SAFE_MATH: Readonly<Omit<Math, 'random'>> = Object.freeze(
  Object.fromEntries(
    Object.getOwnPropertyNames(Math)
      .filter((name) => name !== 'random')
      .map((name) => [name, Math[name as keyof Math]]),
  ) as Omit<Math, 'random'>,
)

const createTicker = (budgetMs: number, now: () => number) => {
  let count = 0
  let frameStart = 0
  const tick = (): void => {
    count += 1
    if (count % TICK_CHECK_INTERVAL === 0 && now() - frameStart > budgetMs)
      throw new OverheatError(budgetMs)
  }
  const startFrame = (): void => {
    count = 0
    frameStart = now()
  }
  return { tick, startFrame }
}

const buildFactory = (source: string): ((...args: unknown[]) => unknown) => {
  const body = `'use strict';\n${source}\nreturn typeof draw === 'function' ? draw : undefined`
  return new Function('__tick', ...SHADOWED_GLOBALS, 'Math', body) as (
    ...args: unknown[]
  ) => unknown
}

/**
 * Instruments plot code and wraps its draw() in a frame budget and a scope
 * where the dangerous globals are gone. This is the budget guard, not the
 * security boundary: that is the worker, the sandbox origin and the CSP.
 */
export const compilePlot = (code: string, options: CompileOptions = {}): CompiledPlot => {
  const budgetMs = options.budgetMs ?? FRAME_BUDGET_MS
  const now = options.now ?? (() => performance.now())
  const source = instrument(code)
  const { tick, startFrame } = createTicker(budgetMs, now)
  const shadows = SHADOWED_GLOBALS.map(() => undefined)
  const userDraw = buildFactory(source)(tick, ...shadows, SAFE_MATH)
  if (typeof userDraw !== 'function') {
    throw new PlotError('plot.js must export a draw(g, t, env) function')
  }
  const draw: DrawFn = (g, t, env) => {
    startFrame()
    ;(userDraw as DrawFn)(g, t, env)
  }
  return { draw, source }
}
